/* eslint-disable max-depth */
import type { trackEcommerce } from '@svenskaspelab/nova-core/nextjs/analytics';

import { BaseError, CancelWagerError, MiddlewarePaymentError } from '../error/index.js';
import { type Middleware, pipeline, subject } from '../utils/entry.js';
import { logger } from '../utils/index.js';
import type { CancelWager } from './Cancel.js';
import { type CancelContextApi, createCancelContextApi } from './context/createCancelContextApi.js';
import { executeCancelRequest } from './executeCancelRequest.js';
import { trackCancelWager } from './trackCancelWagers.js';

type FirstParameter<TFn> = TFn extends (arg: infer R) => unknown ? R : never;
type PreMiddleware = CancelContextApi;
type PostMiddleware = CancelContextApi;

export type PreMiddlewareFn = Middleware<PreMiddleware>;
export type PostMiddlewareFn = Middleware<PostMiddleware>;

export type CancelConfig = {
  trackTransaction?: typeof trackEcommerce;
};

type ObservableLifecycleHooks = {
  onStart: ReturnType<typeof subject>;
  onFinally: ReturnType<typeof subject>;
};

type CancelItemWithError = {
  error?: unknown;
};

const hasErrorProperty = (value: unknown): value is CancelItemWithError => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return 'error' in value;
};

/**
 * This is the main function of the Cancel component which creates an Cancel object that can adopt
 * lifecycle hooks for the cancelation process and add middleware functions to be run before and after
 * the cancel request(s).
 *
 * @param wagersToCancel - array of wagers to cancel
 * @returns - The object to add middlewares, lifecycle hooks and execute the payment request
 * @throws {Error} - Will throw if wagersToCancel is empty
 */
export class Cancel {
  private readonly _wagersToCancel: CancelWager[] = [];

  private readonly _preMiddlewares = pipeline<PreMiddleware>();
  private readonly _postMiddlewares = pipeline<PostMiddleware>();

  private readonly _observers = {
    onStart: subject(),
    onFinally: subject(),
  } as const satisfies ObservableLifecycleHooks;

  private _pendingCancelation?: Promise<
    readonly [WeakMap<CancelWager, unknown>, CancelWagerError | MiddlewarePaymentError | undefined]
  > = undefined;

  private _isCancelationInitializedOnce: boolean = false;

  private readonly _trackTransaction?: typeof trackEcommerce;

  constructor(wagersToCancel: CancelWager[] = [], config?: CancelConfig) {
    if (wagersToCancel.length === 0) {
      throw new Error('Wagers to cancel must be of type CancelWager');
    }
    this._wagersToCancel = wagersToCancel;
    this._trackTransaction = config?.trackTransaction;
  }

  /**
   * Lifecycle hooks (callback methods) to execute in the different states that a payment request has.
   * Adopts the pubsub pattern and the hooks can therefore be specified numerous times for separations of concerns.
   *
   * onStart - executes when the payment process starts, no arguments. Useful for eg. starting a spinner etc.
   *
   * @param observer Observer function to be called when the payment process starts
   * @returns The payment instance for chaining
   */
  onStart(observer: FirstParameter<typeof this._observers.onStart.subscribe>): this {
    this._observers.onStart.subscribe(observer);

    return this;
  }

  /**
   * Lifecycle hooks (callback methods) to execute in the different states that a payment request has.
   * Adopts the pubsub pattern and the hooks can therefore be specified numerous times for separations of concerns.
   *
   * onFinally - always executed last in the payment process, takes two arguments:
   * 1) payment response API
   * 2) error object if there were any error during the payment.
   *
   * @param observer Observer function to be called when the payment process is done
   * @returns The payment instance for chaining
   */
  onFinally(observer: FirstParameter<typeof this._observers.onFinally.subscribe>): this {
    this._observers.onFinally.subscribe(observer);

    return this;
  }

  /**
   * Public function to start the cancelation process.
   *
   * @throws {Error} - Will throw if some sort of validation isn't satisfied before the cancelation starts
   * @returns - A promise that resolves to an array containing two items
   * 1) an object that contains the cancel response
   * 2) the original error instance, is undefined if no error ocurred.
   */
  execute = (): Promise<
    readonly [WeakMap<CancelWager, unknown>, CancelWagerError | MiddlewarePaymentError | undefined]
  > => {
    this._isCancelationInitializedOnce = true;

    if (this._pendingCancelation) {
      // prevent accidental cancelation requests
      logger.info('Cancel already initialized');

      return this._pendingCancelation;
    }

    this._pendingCancelation = this._tryCancel();

    // Run async cancelation
    return this._pendingCancelation;
  };

  /**
   * Adds a middleware function to be executed before the cancelable items will be processed.
   * If one middleware fails no further middleware will be called and the cancelation request(s) will never occur.
   * Useful to create user interaction for eg. customer to confirm cancelation of wager.
   *
   * @param middlewares - A curried function wheres the first function takes the cancelation context api
   * and the second function takes the `next` function to be called to execute
   * the next middleware in the chain.
   * @returns - The cancelation instance for chaining
   * @throws {Error} - Will throw if trying to add middleware after cancelation initialization
   */
  addPreMiddleware(...middlewares: PreMiddlewareFn[]): this {
    if (this._isCancelationInitializedOnce) {
      throw new Error('Not allowed to add middleware after cancelation initialization');
    }
    this._preMiddlewares.push(...middlewares);

    return this;
  }

  /**
   * Adds a middleware function to be executed after the cancelation request(s) settles.
   * At least one cancelable item must have been tried to cancel for a post middleware stack to be run.
   * Useful to show confirmation of cancelation to customer.
   *
   * @param middlewares - A curried function wheres the first function takes the cancel context api
   * and the second function takes the `next` function to be called to execute
   * the next middleware in the chain.
   * @returns - The cancelation instance for chaining
   * @throws {Error} - Will throw if no cancelable items were tried to be canceled before adding post middleware
   */
  addPostMiddleware(...middlewares: PostMiddlewareFn[]): this {
    if (this._isCancelationInitializedOnce) {
      throw new Error('Not allowed to add middleware after cancelation initialization');
    }
    this._postMiddlewares.push(...middlewares);

    return this;
  }

  /**
   * Start the cancelation process and continuously logs the payment process to Kibana.
   * 1) runs pre middlewares
   * 2) executes cancel request through the executeCancelRequest function
   * 3) runs post middlewares
   * 5) runs tracking through the trackCancelWager function
   * 6) payment request over, settles.
   *
   * @returns - A promise that resolves to an array containing two items
   * 1) an object that contains the cancel response
   * 2) the original error instance, is undefined if no error ocurred.
   */
  private async _tryCancel(): Promise<
    [WeakMap<CancelWager, unknown>, CancelWagerError | MiddlewarePaymentError | undefined]
  > {
    const [contextApi, updateContextApi] = createCancelContextApi(this._wagersToCancel);
    let cancelError: CancelWagerError | MiddlewarePaymentError | undefined;

    try {
      logger.info('Cancel wager start', { totalCount: contextApi.count() });

      await this._preMiddlewares.execute(contextApi);

      const successCount = await executeCancelRequest(contextApi, this._wagersToCancel);

      // Update cancel context with success count
      updateContextApi.setCanceledCount(successCount);
    } catch (error) {
      if (error instanceof BaseError) {
        cancelError = error;
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        cancelError = new CancelWagerError({ clientCode: 'GENERIC', message: errorMessage });
        logger.warn('Unhandled exception during cancellation', { error: errorMessage });
      }
    } finally {
      const wagersCanceled = contextApi.getCancelItems();
      let wagerCancelError: CancelWagerError | MiddlewarePaymentError | undefined;

      for (const wager of this._wagersToCancel) {
        const cancelItem = wagersCanceled.get(wager);

        if (hasErrorProperty(cancelItem)) {
          const { error } = cancelItem;

          if (error instanceof CancelWagerError || error instanceof MiddlewarePaymentError) {
            wagerCancelError = error;
            break;
          }
        }
      }

      cancelError = cancelError ?? wagerCancelError;

      if (cancelError instanceof CancelWagerError || cancelError instanceof MiddlewarePaymentError) {
        // Intentional errors
        logger.info('Cancellation exited', { message: cancelError.message, code: cancelError.clientCode });
      }

      const totalCancelItems = this._wagersToCancel.length;
      const successfulCanceledItems = contextApi.getCanceledCount();

      if (successfulCanceledItems) {
        logger.info('Cancel done', { successfulCount: successfulCanceledItems, totalCount: totalCancelItems });
        // Updated account balance if successful cancelation
        // svs.components.account_balance.get(true);
        // run tracking after cancellation
        setTimeout(() => {
          trackCancelWager(contextApi, this._trackTransaction);
        });
      }

      delete this._pendingCancelation;
    }

    this._postMiddlewares.execute(contextApi /* , this._wagersToCancel */).catch((error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.warn('Post middleware failed', { error: errorMessage });
    });

    const response: [WeakMap<CancelWager, unknown>, CancelWagerError | MiddlewarePaymentError | undefined] = [
      contextApi.getCancelItems(),
      cancelError,
    ];
    this._observers.onFinally.publish(response);
    return response;
  }
}

/**
 * Creates a cancellation instance to set up middlewares, lifecycle hooks and execute the cancellation process.
 *
 * @param wagersToCancel - array of wagers to cancel
 * @param config - Optional cancellation config
 * @returns - The object to add middlewares, lifecycle hooks and execute the cancellation request
 */
export function createCancellation(wagersToCancel: CancelWager[], config?: CancelConfig): Cancel {
  return new Cancel(wagersToCancel, config);
}
